import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { AxiosError } from 'axios';
import { EventType, HistoryVisibility, ICreateRoomStateEvent, JoinRule, Room } from 'matrix-js-sdk/src/matrix';
import { useState, useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { createMeeting } from 'api/meedioBackend/createMeeting';
import { updateMeeting } from 'api/meedioBackend/updateMeeting';
import { useCalendarSyncContext } from 'contexts/CalendarSyncProvider/CalendarSyncProvider';
import useFeatureFlagContext from 'contexts/FeatureFlagProvider/useFeatureFlagContext';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useMeetingGroupChatCreation from 'hooks/roomCreation/useMeetingGroupChatCreation';
import useRoomAccessInitialState from 'hooks/roomCreation/useRoomAccessInitialState';
import useRoomCreationPowerLevels from 'hooks/roomCreation/useRoomCreationPowerLevels';
import useRoomManagersInitialState from 'hooks/roomCreation/useRoomManagersInitialState';
import { getChatMeetingCreatePermissionsForMessengerRoom } from 'hooks/useChatMeetingCreatePermissions/useChatMeetingCreatePermissions';
import { buildMeetingInitialStates, encryptionState } from 'hooks/useCreateMeeting/utils';
import { buildWorkspaceMeetingCreationContext } from 'hooks/useCreateMeeting/workspaceCreationParams';
import useCreateRoom from 'hooks/useCreateRoom/useCreateRoom';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import useIsRoomInCurrentWorkspace from 'hooks/useIsRoomInCurrentWorkspace';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import useScopedUserPowerLevels from 'hooks/useScopedUserPowerLevels/useScopedUserPowerLevels';
import {
  addNewEventToCalendar,
  getPrimaryCalendarInformation,
} from 'modules/CalendarView/CalendarSync/CalendarSyncModal/calendarHelpers';
import { storageUtils } from 'modules/CalendarView/CalendarSync/CalendarSyncModal/utils';
import { sendScheduleStateEvent } from 'modules/CreateNewRoomModal/CreateRoomStepModal/sendScheduleStateEvent';
import {
  isEmailFormat,
  isMatrixIdFormat,
} from 'modules/CreateWorkspaceModal/WorkspaceInviteStep/WorkspaceInviteForm/utils';
import useMatrixInviteeValidation from 'modules/Messenger/hooks/useMatrixInviteeValidation';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { serializeRecurrence } from 'shared/components/ScheduleVideoMeetingModal/RecurrenceSelect';
import { FeatureFlag } from 'utils/Constants';
import logger from 'utils/logger';
import {
  buildMeetingSpaceChatChildInitialState,
  getViaServers,
  inviteUsersToRoom,
  sendChildRoomParentStates,
} from 'utils/matrixUtils';
import { getMessengerRoomTeamMeetingFlag } from 'utils/messengerRoomAccess';
import retryPromise from 'utils/retryPromise';
import { getCanonicalParentRoom } from 'utils/roomHierarchyUtils';

import { findConflictingGroupChats } from './findConflictingGroupChats';
import GroupChatConflictModal from './GroupChatConflictModal';
import { postLinkMessengerRoom } from './postLinkMessengerRoom';
import { ScheduleMeetingFormValues } from './types';
import { applyTimezoneOffset } from './utils';

interface UseScheduleMeetingSubmitParams {
  messengerRoomId?: string;
  form: UseFormReturn<ScheduleMeetingFormValues>;
  setCurrentStep: (step: 1 | 2) => void;
}

const is409AliasTaken = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) return false;
  return error.response?.status === 409 && (error.response?.data as { error?: string })?.error === 'alias_taken';
};

const useScheduleMeetingSubmit = ({ messengerRoomId, form, setCurrentStep }: UseScheduleMeetingSubmitParams) => {
  const { t } = useTranslation();
  const modal = useModal();
  const { matrixClient } = useMatrixContext();
  const { pushToast } = useToast();
  const { mainCalendarInfo, accountCalendars } = useCalendarSyncContext();
  const { workspaceId, workspacePermissions } = useWorkspacesContext();
  const { createRoom } = useCreateRoom();
  const { isEnabled } = useFeatureFlagContext();
  const isRoomE2EEEnabled = isEnabled(FeatureFlag.ROOM_E2EE);
  const pushNoPermissionToast = useNoPermissionToast();
  const roomManagersInitialState = useRoomManagersInitialState(workspaceId);
  const { ownerPowerLevel: meetingOwnerPowerLevel } = useRoomCreationPowerLevels('meeting');
  // BUG-003: the auto-created group chat is a messenger room → chat-scoped power levels (mirrors
  // /messenger), pre-bound by useMeetingGroupChatCreation. The meeting space + dedicated chat-child
  // rooms keep the meeting scope above.
  const { createMeetingGroupChat } = useMeetingGroupChatCreation();
  const teamRoomAccessInitialState = useRoomAccessInitialState('team');
  const { filterInvalidMatrixInvitees } = useMatrixInviteeValidation();
  const messengerRoom = messengerRoomId ? matrixClient.getRoom(messengerRoomId) : null;
  const workspaceRoomId = messengerRoom ? getCanonicalParentRoom(messengerRoom)?.roomId ?? null : null;
  const scopedPowerLevels = useScopedUserPowerLevels(workspaceRoomId);
  const { getIsRoomInCurrentWorkspace } = useIsRoomInCurrentWorkspace();
  const isScheduledEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_MESSENGER_SCHEDULED_MEETINGS);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (messengerRoomId && !isScheduledEnabled) return;
    if (messengerRoomId) {
      const { canScheduleMeeting } = getChatMeetingCreatePermissionsForMessengerRoom(
        matrixClient,
        messengerRoomId,
        scopedPowerLevels.chat,
        getIsRoomInCurrentWorkspace(messengerRoomId)
      );
      if (!canScheduleMeeting) {
        pushNoPermissionToast();
        return;
      }
    }

    // Defence in depth (R1.4): block non-messenger schedule-submit when the workspace role lacks the
    // scheduled-meeting permission. Messenger mode keeps its own canScheduleMeeting gate above.
    if (!messengerRoomId && workspaceId && !workspacePermissions.createScheduledMeeting) {
      pushNoPermissionToast();
      return;
    }

    const formValues = form.getValues();
    setIsSubmitting(true);

    // BUG-004: pre-validate Matrix invitees once (drop non-existent IDs + warn) before any room/meeting
    // creation — mirrors the /messenger path. The validated list feeds add_guest_mxids, conflict
    // detection, the auto-created group chat, and the conflict modal, so there is a single existence
    // round-trip per submit. Done before room creation so a failure leaves no orphaned rooms.
    let validMxids: string[];
    try {
      validMxids = await filterInvalidMatrixInvitees(formValues.participants.filter(isMatrixIdFormat));
    } catch (error) {
      logger.error('useScheduleMeetingSubmit: failed to validate Matrix invitees', { error });
      pushToast({ variant: 'error', title: t('something_went_wrong_try_again_later') });
      setIsSubmitting(false);
      return;
    }

    const startDate = formValues.startDate!;
    const startTime = formValues.startTime!;
    const endDate = formValues.endDate!;
    const endTime = formValues.endTime!;

    const startDateTime = new Date(startDate);
    startDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

    const endDateTime = new Date(endDate);
    endDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);

    const scheduledAtUtc = applyTimezoneOffset(startDateTime, formValues.timezone);
    const endsAtUtc = applyTimezoneOffset(endDateTime, formValues.timezone);

    const retryOpts = {
      retries: 3,
      delayMs: (n: number) => 500 * 2 ** n,
      shouldRetry: (err: unknown) => !is409AliasTaken(err),
    };

    // Workspace parity (R2.2/R5): bot + meeting overrides + creator PL 99 on the space room, plus team
    // access + managers events. Personal workspace (workspaceId === null) stays byte-for-byte unchanged.
    const { managersStates, accessStates, workspaceCreationParams } = buildWorkspaceMeetingCreationContext({
      workspaceId,
      roomManagersInitialState,
      teamRoomAccessInitialState,
      meetingOwnerPowerLevel,
    });

    // Create the dedicated chat child room before the space room so its ID can be embedded in the
    // space's m.space.child initial state (mirrors useCreateMeeting / Path A). Only the Video Hub
    // path without a group chat needs this. When opened from a Messenger chat, postLinkMessengerRoom
    // links the existing messenger room; when the group-chat toggle is on, the group chat created
    // later by the shared createMessengerRoom + postLinkMessengerRoom becomes the chat child. In both
    // cases a dedicated room would leave a duplicate empty chat that useJoinChatRoom resolves first.
    let chatRoomId: string | undefined;
    if (!messengerRoomId && !formValues.groupChat) {
      try {
        const chatPreliminaryStates: ICreateRoomStateEvent[] = [
          {
            type: EventType.RoomHistoryVisibility,
            state_key: '',
            content: { history_visibility: HistoryVisibility.Invited },
          },
          ...managersStates,
          ...(isRoomE2EEEnabled ? [encryptionState] : []),
        ];

        const chatRoom = await retryPromise({
          fn: () =>
            createRoom({
              title: formValues.name.trim(),
              customInitialStates: chatPreliminaryStates,
              ...workspaceCreationParams,
            }),
          ...retryOpts,
          label: 'createChatRoom',
        });

        if (!chatRoom) throw new Error('createChatRoom returned no room');
        chatRoomId = chatRoom.room_id;
      } catch (error) {
        logger.error('useScheduleMeetingSubmit: createChatRoom failed', { error });
        pushToast({ variant: 'error', title: t('messenger.schedule_meeting_create_room_failed') });
        setIsSubmitting(false);
        return;
      }
    }

    // Create Matrix space room
    let matrixRoomId: string;
    try {
      const meetingStates = buildMeetingInitialStates({
        isPrivateRoom: false,
        isWaitingListEnabled: false,
        workspaceId: workspaceId ?? null,
        matrixClient,
        isRoomE2EEEnabled,
      });

      const space = await retryPromise({
        fn: () =>
          createRoom({
            title: formValues.name.trim(),
            customCreationContent: { type: 'm.space' },
            customInitialStates: [
              ...meetingStates,
              ...managersStates,
              ...accessStates,
              ...(chatRoomId ? [buildMeetingSpaceChatChildInitialState(chatRoomId)] : []),
            ],
            ...workspaceCreationParams,
          }),
        ...retryOpts,
        label: 'createRoom',
      });

      if (!space) throw new Error('createRoom returned no room');
      matrixRoomId = space.room_id;
    } catch (error) {
      logger.error('useScheduleMeetingSubmit: createRoom failed', { error });
      pushToast({ variant: 'error', title: t('messenger.schedule_meeting_create_room_failed') });
      if (chatRoomId) {
        matrixClient
          .leave(chatRoomId)
          .catch((leaveError) =>
            logger.warn('useScheduleMeetingSubmit: failed to leave orphan chat room', { leaveError })
          );
      }
      setIsSubmitting(false);
      return;
    }

    // Link the dedicated chat child room to the space (join rules + space parent); non-blocking per
    // spec — the chat is already discoverable via the space's m.space.child initial state. Skipped
    // on the messenger-linked path, which has no dedicated chat room (postLinkMessengerRoom links
    // the existing messenger room instead).
    if (chatRoomId) {
      try {
        await sendChildRoomParentStates(matrixClient, chatRoomId, matrixRoomId, JoinRule.Restricted);
      } catch (error) {
        logger.warn('useScheduleMeetingSubmit: sendChildRoomParentStates failed (non-blocking)', { error });
      }
    }

    // Register the meeting space as a child of the workspace (R2.2); fire-and-forget per spec.
    if (workspaceId) {
      const viaServers = getViaServers(workspaceId, matrixClient);
      matrixClient
        .sendStateEvent(workspaceId, EventType.SpaceChild, { via: viaServers, suggested: true }, matrixRoomId)
        .catch((error: unknown) =>
          logger.warn('useScheduleMeetingSubmit: failed to register SpaceChild on workspace', { error })
        );
    }

    // Split participants into Matrix IDs and email addresses. BUG-004: mxids is the pre-validated list
    // (invalid Matrix IDs already dropped + warned above); emails need no existence check.
    const mxids = validMxids;
    const emails = formValues.participants.filter(isEmailFormat);

    // Shared-meeting coercion (R4.2): a workspace user without invite permission cannot create a shared
    // meeting, so the meeting is forced to a team meeting regardless of the (hidden) toggle's value.
    const canCreateSharedMeeting = !workspaceId || workspacePermissions.invitePeople;
    // BUG-001: a team meeting requires a workspace to populate workspace_matrix_room_id. In the
    // personal workspace neither the active workspaceId nor the messenger room's canonical-parent
    // workspace exists, so the meeting must not be flagged as a team meeting — the backend rejects
    // is_team_meeting:true without workspace_matrix_room_id. effectiveWorkspaceId is the single source
    // for both the flag and the payload field below so they can never disagree.
    const effectiveWorkspaceId = workspaceRoomId ?? workspaceId ?? null;
    const isTeamMeeting =
      !!effectiveWorkspaceId &&
      (messengerRoomId
        ? getMessengerRoomTeamMeetingFlag(matrixClient, messengerRoomId)
        : !canCreateSharedMeeting || formValues.isOrganisationMeeting);

    // POST /api/meetings
    try {
      const meeting = await retryPromise({
        fn: () =>
          createMeeting({
            matrix_room_id: matrixRoomId,
            scheduled_at: scheduledAtUtc,
            ends_at: endsAtUtc,
            messenger_room_id: messengerRoomId ?? undefined,
            room_name: formValues.name.trim(),
            message: formValues.description.trim() || undefined,
            alias: `${formValues.slug}-${formValues.slugPostfix}`,
            is_recurring: formValues.recurrence !== 'none',
            recurrence_rule: serializeRecurrence(formValues.recurrence, startDateTime) ?? undefined,
            add_guest_mxids: mxids.length > 0 ? mxids : undefined,
            add_guests: emails.length > 0 ? emails : undefined,
            is_team_meeting: isTeamMeeting,
            workspace_matrix_room_id: effectiveWorkspaceId ?? undefined,
          }),
        ...retryOpts,
        label: 'createMeeting',
      });

      try {
        await sendScheduleStateEvent({
          client: matrixClient,
          roomId: matrixRoomId,
          startsAt: scheduledAtUtc,
          endsAt: endsAtUtc,
        });
      } catch (error) {
        logger.warn('useScheduleMeetingSubmit: sendScheduleStateEvent failed', { error });
      }

      // Mirror useCreateMeeting: create the external calendar event when sync is connected, so the
      // scheduled meeting is visible via Open in Calendar (#8098). Calendar errors must never fail
      // the schedule — keep this fully isolated from the surrounding meeting-creation flow.
      try {
        const { primaryCalendarId, token, isTokenExpired } = getPrimaryCalendarInformation(
          accountCalendars,
          mainCalendarInfo
        );
        if (primaryCalendarId && token && !isTokenExpired) {
          const provider = storageUtils.getProviderFromToken(token);
          const meetingUrl = `${window.location.origin}/meetings/${matrixRoomId}`;
          const { error } = await addNewEventToCalendar(
            provider,
            primaryCalendarId,
            {
              title: formValues.name.trim(),
              description: formValues.description.trim() || '',
              location: meetingUrl,
              provider,
              calendarId: primaryCalendarId,
              startDate: scheduledAtUtc,
              endDate: endsAtUtc,
            },
            token.access_token,
            matrixRoomId
          );
          if (error) pushToast({ variant: 'warning', title: t('event_creation_error') });
        }
      } catch (error) {
        logger.warn('useScheduleMeetingSubmit: addNewEventToCalendar failed', { error });
        pushToast({ variant: 'warning', title: t('event_creation_error') });
      }

      // When opened from a Messenger chat: link the meeting (now includes restricted join rules)
      // then invite email participants to the Messenger room so they can access the meeting
      // via chat membership — not via a direct meeting-room invite that bypasses the gate.
      // When no Messenger link: invite emails directly to the Matrix meeting room (existing path).
      if (messengerRoomId) {
        await postLinkMessengerRoom(matrixClient, matrixRoomId, messengerRoomId, meeting, formValues.name.trim());
        for (const email of emails) {
          await matrixClient.inviteByEmail(messengerRoomId, email).catch((error) => {
            logger.warn('useScheduleMeetingSubmit: inviteByEmail to messenger room failed', { email, error });
          });
        }
      } else {
        // Invite email participants to the Matrix meeting room via 3PID (non-chat-linked path)
        for (const email of emails) {
          await matrixClient.inviteByEmail(matrixRoomId, email).catch((error) => {
            logger.warn('useScheduleMeetingSubmit: inviteByEmail failed', { email, error });
          });
        }
        // Matrix-ID guests need an explicit room invite — add_guest_mxids alone does not create
        // KnownMembership.Invite on the client (mirrors InviteScheduledMeetingParticipantsModal).
        if (mxids.length > 0 && !formValues.groupChat) {
          const { failedInvites } = await inviteUsersToRoom(matrixClient, matrixRoomId, mxids);
          if (failedInvites.length > 0) {
            logger.warn('useScheduleMeetingSubmit: some mxid invites to meeting space failed', { failedInvites });
          }
        }
      }

      // Conflict detection — only when groupChat toggle ON and no messengerRoomId prop
      if (formValues.groupChat && !messengerRoomId) {
        let conflicts: Room[];
        try {
          conflicts = findConflictingGroupChats(matrixClient, mxids, workspaceId);
        } catch (error) {
          logger.warn('useScheduleMeetingSubmit: conflict check failed, skipping', { error });
          conflicts = [];
        }

        if (conflicts.length === 0) {
          try {
            const newRoom = await retryPromise({
              // BUG-003: chat-scoped power levels for the messenger group chat (mirrors /messenger),
              // pre-bound by useMeetingGroupChatCreation.
              fn: () => createMeetingGroupChat({ participantMxids: mxids }),
              ...retryOpts,
              label: 'createMessengerRoom',
            });
            await retryPromise({
              fn: () => updateMeeting(meeting.id, { messenger_room_id: newRoom.roomId }),
              ...retryOpts,
              label: 'updateMeeting-groupChat',
            });
            await postLinkMessengerRoom(matrixClient, matrixRoomId, newRoom.roomId, meeting, formValues.name.trim());
          } catch (error) {
            logger.warn('useScheduleMeetingSubmit: auto-create messenger room failed', { error });
          }
        } else {
          const conflictResult = (await NiceModal.show(GroupChatConflictModal, {
            meetingId: meeting.id,
            conflicts,
            participantMxids: mxids,
          })) as { action: string; roomId: string } | undefined;
          if (conflictResult?.roomId) {
            await postLinkMessengerRoom(
              matrixClient,
              matrixRoomId,
              conflictResult.roomId,
              meeting,
              formValues.name.trim()
            );
          }
        }
      }

      modal.resolve(meeting);
      modal.remove();
    } catch (error) {
      if (is409AliasTaken(error)) {
        setCurrentStep(1);
        form.setError('slug', { message: 'schedule_meeting.alias_taken_error' });
        setIsSubmitting(false);
        return;
      }
      pushToast({ variant: 'error', title: t('schedule_meeting.submit_failed_retry_later') });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    form,
    matrixClient,
    accountCalendars,
    mainCalendarInfo,
    workspaceId,
    workspaceRoomId,
    workspacePermissions,
    messengerRoomId,
    createRoom,
    createMeetingGroupChat,
    filterInvalidMatrixInvitees,
    isRoomE2EEEnabled,
    isScheduledEnabled,
    pushToast,
    pushNoPermissionToast,
    roomManagersInitialState,
    meetingOwnerPowerLevel,
    teamRoomAccessInitialState,
    scopedPowerLevels.chat,
    getIsRoomInCurrentWorkspace,
    t,
    modal,
    setCurrentStep,
  ]);

  return { handleSubmit, isSubmitting };
};

export { is409AliasTaken };
export default useScheduleMeetingSubmit;
